/**
 * <s-cart-add>: add-on "Add" buttons that live inside the cart page and cart drawer
 * (roll-on oil add-ons, "also bought" rail in the drawer).
 *
 * These use the classic additive endpoint (POST /cart/add.js), which only ever adds the
 * line to the shopper's existing cart, then refreshes the mounted cart partials. This avoids
 * the add replacing the cart that is currently on screen. Without JS the form still posts
 * natively to /cart/add.
 */
import { announceCartFeedback, getCartErrorMessage } from '@theme/cart-feedback';

class SCartAdd extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('submit', (event) => this.#onSubmit(event), { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    event.preventDefault();
    if (this.getAttribute('aria-busy') === 'true') return;

    const button = event.submitter instanceof HTMLElement ? event.submitter : form.querySelector('[type="submit"]');
    const error = this.querySelector('[data-add-to-cart-error]') || (form.dataset.errorId && document.getElementById(form.dataset.errorId));
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }
    this.setAttribute('aria-busy', 'true');
    button?.setAttribute('aria-disabled', 'true');
    button?.classList.add('opacity-60');

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.description || data.message || '');
      }

      await customElements.whenDefined('s-cart').catch(() => {});
      const cart = customElements.get('s-cart');
      if (cart?.refreshMountedCarts) {
        await cart.refreshMountedCarts();
        // Opt-in (e.g. catalog cards outside the cart): open the cart drawer so the shopper sees the add.
        if (this.hasAttribute('data-open-cart')) await window.Shopify?.actions?.openCart?.();
      } else {
        window.location.reload();
      }
    } catch (err) {
      const message = err?.message || getCartErrorMessage(err) || '';
      if (error && message) {
        error.textContent = message;
        error.hidden = false;
      } else if (message) {
        announceCartFeedback(message, { toast: true });
      }
    } finally {
      this.removeAttribute('aria-busy');
      button?.removeAttribute('aria-disabled');
      button?.classList.remove('opacity-60');
    }
  }
}

if (!customElements.get('s-cart-add')) customElements.define('s-cart-add', SCartAdd);
